import { useEffect, useState, useRef } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { GPT_WSS, WSS_ENCRYPTION_KEY } from '@Env/variables';
import { toGPTMessage } from './websockets';
import ChatBubble from './ChatBubble';
import { useChatHistory } from '@Context/ChatHistoryContext';
import { ChatHistory, Conversation } from '@Models/types';
import { BounceLoader, MoonLoader } from 'react-spinners';
import colors from '@Common/assets/colors';
import { decrypt, encrypt } from '@Lib/encryption';
import { useUtilityContext } from '@Context/prompt-toolbox/UtilityContext';
import { useAuth } from '@Context/CognitoAuthProvider';
import showToast from '@Common/toast/showToast';
import { PaperclipHorizontal } from '@phosphor-icons/react';
import FileAttachmentDisplay from '../../../common/FileAttachmentDisplay';
import {
  createMessageWithAttachments,
  validatePromptBeforeSend,
  handleFileSelectionUpload,
  removeAttachmentAtIndex,
  clearAllAttachments,
  UploadResult,
} from '@Utils/FileAttachmentUtils';

export interface GPTMessage {
  sender: 'you' | 'gpt';
  message: string;
  dateSent: Date;
  fileName?: string[];
}

interface Props {}

const MAX_RECONNECT = 3;

const GPT: React.FC<Props> = () => {
  const [messages, setMessages] = useState<GPTMessage[]>([]);
  const [currentStreamMessage, setCurrentStreamMessage] =
    useState<GPTMessage>();
  const [prompt, setPrompt] = useState('');
  const [isConnected, setIsConencted] = useState(false);
  const [WssAuthError, setWssAuthError] = useState(false);
  const { loaders } = useUtilityContext();
  const [isReconnecting, setIsReconnecting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { logout, refreshIfExpired } = useAuth();
  const [attachments, setAttachments] = useState<UploadResult[]>([]);
  const reconnectOnAbnormalClose = useRef(0);

  const { sendMessage, lastMessage, readyState } = useWebSocket(GPT_WSS, {
    onOpen: async () => {
      try {
        await refreshIfExpired(localStorage.getItem('accessToken'));
      } catch {
        logout();
      }
      console.log('[ socket: OPENED ]');
      sendMessage(
        JSON.stringify({
          action: 'sendmsgtest',
          token: localStorage.getItem('accessToken'),
        })
      );
    },
    onClose: (event) => {
      console.log('[ socket: CLOSED ]', event.code, event.reason);
      if (event.code === 1006) {
        reconnectOnAbnormalClose.current += 1;
      }
      if (
        event.code === 1008 ||
        reconnectOnAbnormalClose.current >= MAX_RECONNECT ||
        WssAuthError
      ) {
        showToast.error(
          'Session invalid. Please log in again to continue using the app.',
          'wss-auth-error'
        );

        logout();
      }
    },
    shouldReconnect: () =>
      true && reconnectOnAbnormalClose.current < MAX_RECONNECT,
    reconnectAttempts: MAX_RECONNECT,
  });
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [processingPrompt, setProcessingPrompt] = useState<string | null>(null);
  const [processing, setProcessing] = useState<boolean>(false);
  const [inputTokens, setInputTokens] = useState<number>(0);
  const [outputTokens, setOutputTokens] = useState<number>(0);
  const [totalTokens, setTotalTokens] = useState<number>(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { state, dispatch, fetchChatMessages, fetchAITokenUsage } =
    useChatHistory();
  const { chatHistories, currentChatId } = state;

  const chats = chatHistories.find((chat) => chat.id === currentChatId);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleResponseAction = (
    chatHistory: ChatHistory,
    conversation: Conversation
  ) => {
    const conversationExists =
      chatHistories.findIndex((history) => history.id == chatHistory.id) != -1;
    if (!conversationExists) {
      dispatch({ type: 'ADD_CHAT_HISTORY', payload: chatHistory });
      dispatch({
        type: 'SET_CURRENT_CHAT',
        payload: { id: chatHistory.id },
      });
    } else if (conversationExists && currentChatId !== chatHistory.id) {
      dispatch({
        type: 'SET_CURRENT_CHAT',
        payload: { id: chatHistory.id },
      });
      dispatch({
        type: 'ADD_CONVERSATION',
        payload: {
          chatId: chatHistory.id,
          conversation: conversation,
        },
      });
    } else {
      dispatch({
        type: 'ADD_CONVERSATION',
        payload: {
          chatId: chatHistory.id,
          conversation: conversation,
        },
      });
    }
  };

  const resetProcessing = () => {
    setProcessing(false);
    setProcessingId(null);
  };

  useEffect(() => {
    if (lastMessage !== null) {
      try {
        const dataObj = JSON.parse(lastMessage.data);
        const { action, type, message } = dataObj;
        let messageObj;
        try {
          messageObj = JSON.parse(message);
        } catch (parseError) {
          console.warn(
            'Failed to parse message as JSON, using raw message:',
            message
          );
          messageObj = { details: message };
        }
        if (action === 'processing') {
          setProcessing(messageObj.loading);
          setProcessingId(messageObj.loading ? processingId : null);
        } else if (action === 'error') {
          if (type === 'auth-error') {
            setWssAuthError(true);
          } else if (type !== 'auth-timeout') {
            const responseMessage: GPTMessage = {
              sender: 'gpt',
              message: `ERROR‼️ ${messageObj.details}\n Please contact your system administrator.`,
              dateSent: new Date(),
            };
            resetProcessing();
            setCurrentStreamMessage(undefined);
            if (
              processingId == currentChatId ||
              (!currentChatId && !processingId)
            )
              setMessages((prev) => [...prev, responseMessage]);
          }
        } else if (action === 'stream') {
          decrypt(messageObj.response, WSS_ENCRYPTION_KEY).then((data) => {
            let text = typeof data === 'string' ? data : '';
            let parsed: { text?: string; content?: string } = {};
            if (text.trim().length > 0) {
              try {
                parsed = JSON.parse(text);
              } catch {
                parsed = {};
              }
            }
            text = parsed.text || parsed.content || text;
            if (!currentStreamMessage) {
              const dateSent = messageObj.dateTime
                ? new Date(messageObj.dateTime)
                : new Date();
              const responseMessage: GPTMessage = {
                sender: 'gpt',
                message: text as string,
                dateSent: dateSent,
              };
              setCurrentStreamMessage(responseMessage);
              if (
                processingId == currentChatId ||
                (!currentChatId && !processingId)
              ) {
                setMessages((prev) => [...prev, responseMessage]);
              }
            } else {
              const updatedStreamMessage = {
                ...currentStreamMessage,
                message:
                  (currentStreamMessage.message ?? '') + (text as string),
              };
              setCurrentStreamMessage(updatedStreamMessage);
              if (
                processingId == currentChatId ||
                (!currentChatId && !processingId)
              ) {
                setMessages((prevItems) => [
                  ...prevItems.slice(0, -1),
                  updatedStreamMessage,
                ]);
              }
            }
          });
        } else if (action === 'stream_end') {
          const tokens = messageObj.tokens;
          const calcInputTokens = tokens.promptTokens;
          const calcOutputTokens = outputTokens + tokens.completionTokens;
          const calcTotalTokens = calcInputTokens + calcOutputTokens;
          setInputTokens(calcInputTokens);
          setOutputTokens(calcOutputTokens);
          setTotalTokens(calcTotalTokens);

          resetProcessing();
        } else if (action === 'stream_done') {
          decrypt(messageObj.response, WSS_ENCRYPTION_KEY).then((data) => {
            const msgObj = JSON.parse(data as string);
            const buildConvo: Conversation = {
              ...msgObj.conversation,
              prompt: processingPrompt,
              result:
                currentStreamMessage?.message ?? msgObj.conversation.result,
            };
            const buildHistory: ChatHistory = {
              ...msgObj.history,
              messages: [buildConvo],
            };
            handleResponseAction(buildHistory, buildConvo);

            setCurrentStreamMessage(undefined);
            setProcessingPrompt(null);
          });
        }
      } catch (err) {
        console.error('Error parsing message data:', err);
      }
    }
  }, [lastMessage]);

  useEffect(() => {
    if (chats) {
      const transformedMessages: GPTMessage[] = chats.messages
        .map((msg) => [
          {
            sender: 'you' as const,
            message: msg.prompt,
            dateSent: new Date(msg.timestamp),
            fileName: msg.fileName ? msg.fileName.split(', ') : undefined,
          },
          {
            sender: 'gpt' as const,
            message: msg.result,
            dateSent: new Date(msg.timestamp),
            fileName: msg.fileName ? msg.fileName.split(', ') : undefined,
          },
        ])
        .flat();

      setMessages(transformedMessages);
    } else {
      setMessages([]);
    }
  }, [chats]);

  useEffect(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    clearAllAttachments(setAttachments);
  }, [currentChatId]);

  const resetTokens = () => {
    setInputTokens(0);
    setOutputTokens(0);
    setTotalTokens(0);
  };

  useEffect(() => {
    resetTokens();
    if (!state.currentChatId) return;
    if (chats?.messages.length == 0) fetchChatMessages(state.currentChatId);

    fetchAITokenUsage(state.currentChatId).then((data) => {
      setInputTokens(data?.latestPromptTokens ?? 0);
      setOutputTokens(data?.totalCompletionTokens ?? 0);
      setTotalTokens(data?.totalTokens ?? 0);
    });
  }, [state.currentChatId, fetchChatMessages, fetchAITokenUsage]);

  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      setIsConencted(true);
      setIsReconnecting(false);
    } else if (readyState === ReadyState.CLOSED && isConnected) {
      console.log('Socket Disconnected');
      setIsReconnecting(true);
    } else {
      setIsReconnecting(false);
    }
  }, [readyState]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, readyState]);

  const pushMessages = (message: GPTMessage) => {
    setMessages([...messages, message]);
  };

  const bucketSubName = 'ask-ai';
  const handleFileSelection = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files || []);
    await handleFileSelectionUpload(
      files,
      logout,
      setProcessing,
      setAttachments,
      () => {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      },
      bucketSubName
    );
  };

  const removeAttachment = (index: number) => {
    removeAttachmentAtIndex(index, setAttachments);
  };

  const handleSend = async () => {
    if (!validatePromptBeforeSend(prompt, attachments)) {
      return;
    }

    try {
      setProcessingId(currentChatId);

      let messageText = prompt;

      if (attachments.length > 0) {
        messageText = createMessageWithAttachments(prompt, attachments);
      }

      const encryptedMessage = await encrypt(messageText, WSS_ENCRYPTION_KEY);

      sendMessage(
        toGPTMessage(encryptedMessage, currentChatId, 'conversation')
      );
      setProcessingPrompt(messageText);
      const message: GPTMessage = {
        sender: 'you',
        message: messageText,
        dateSent: new Date(),
        fileName:
          attachments.length > 0
            ? attachments.map((a) => a.fileName)
            : undefined,
      };
      pushMessages(message);
      setPrompt('');
      clearAllAttachments(setAttachments);
    } catch (error) {
      console.error('Error sending message:', error);
      showToast.error('Failed to send message');
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (prompt === '' && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [prompt]);

  return (
    <div className="relative mx-2 h-full w-full pb-2 md:mx-4 md:pb-4">
      {isReconnecting && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-y-3 rounded-xl bg-black opacity-90">
          <div className="flex flex-col items-center gap-y-2 rounded-md p-4">
            <MoonLoader color="white" />
            <label className="text-4xl font-bold text-white">
              Chat Session Ended
            </label>
            <label className="animate-pulse text-xl font-extrabold text-primary-500">
              Attempting to reconnect
            </label>
          </div>
        </div>
      )}
      <div className="relative flex h-full w-full flex-col">
        {/* Token Count */}
        <div className="absolute left-1/2 top-2 flex -translate-x-1/2 transform gap-10 text-xs text-gray-400 md:text-base">
          <label>
            Input Tokens: <span>{inputTokens}</span>
          </label>
          <label>
            Output Tokens: <span>{outputTokens}</span>
          </label>
          <label>
            Total Tokens: <span>{totalTokens}</span>
          </label>
        </div>
        <div className="md:custom-scroll h-0 flex-grow scroll-m-0 overflow-y-auto overflow-x-hidden">
          {!isConnected || loaders.chatMessages ? (
            <div className="flex h-full items-center justify-center text-center">
              <MoonLoader size={100} color="#071D49" />
            </div>
          ) : messages.length > 0 ? (
            <>
              {messages.map((message, index) => (
                <ChatBubble key={index} message={message} />
              ))}
              <div ref={messagesEndRef} />
            </>
          ) : (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <label className="text-4xl font-bold text-[#808ba150]">
                New Conversation
              </label>
              <label className="mt-1 text-xl font-extrabold text-blue-950">
                What can I help you with?
              </label>
            </div>
          )}
        </div>
        <form
          onSubmit={(e) => e.preventDefault()}
          className="relative flex items-end space-x-2"
        >
          <div className="relative flex flex-grow flex-col">
            <FileAttachmentDisplay
              attachments={attachments}
              onRemoveAttachment={removeAttachment}
            />
            <div className="relative flex items-center">
              <textarea
                data-testid="message-gpt-area"
                ref={textareaRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = `${Math.min(target.scrollHeight, 200)}px`;
                }}
                placeholder="Ask AI"
                className="custom-scroll mx-1 h-auto w-full resize-none overflow-y-auto rounded-lg border border-white px-2 pt-1 text-primary-700 placeholder:italic focus:outline-primary-400 md:mx-2"
              />
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelection}
                multiple
                accept=".csv,.doc,.docx,.gif,.html,.jpg,.jpeg,.json,.md,.pdf,.png,.txt,.webp,.xls,.xlsx,.xml,.yaml,.yml"
                className="hidden"
              />
              <button
                data-testid="image-upload"
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={processing}
                className="absolute right-5 top-3"
              >
                <PaperclipHorizontal
                  size={20}
                  className={`rotate-90 ${
                    processing
                      ? 'text-gray-300'
                      : 'text-primary-700 hover:text-primary-500'
                  }`}
                />
              </button>
            </div>
          </div>
          <button
            data-testid="send"
            type="submit"
            onClick={handleSend}
            disabled={
              !isConnected ||
              (!prompt.trim() && attachments.length === 0) ||
              Boolean(processing)
            }
            className="flex items-center gap-2 rounded-lg border bg-blue-200 px-2 py-2 text-sm text-blue-900 hover:bg-blue-500 hover:text-white disabled:bg-gray-200 disabled:text-gray-400 md:px-4 md:py-2 md:text-lg"
          >
            {processing && (
              <>
                <BounceLoader size={20} color={colors.brandWarning[500]} />
                <span>Processing...</span>
              </>
            )}
            {!processing && 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default GPT;
